<script setup>
import TransitionFadeIn from '@/components/shared/transitions/TransitionFadeIn.vue'
import AppLoader from '@/components/app/AppLoader.vue'
import AppError from '@/components/app/AppError.vue'
import FlashMessageContainer from '@/components/shared/core/FlashMessageContainer.vue'
import {openUrl} from '@/helpers/url.js'
import {useEnv} from '@/composables/use-env.js'
import {useTeams} from '@/composables/use-teams.js'
import {useLoading} from '@/composables/use-loading.js'
import {useRouting} from '@/composables/use-routing.js'
import {useDocument} from '@/composables/use-document.js'
import {useAppMount} from '@/composables/use-app-mount.js'
import {useAppTheme} from '@/composables/use-app-theme.js'
import {useRailsUrls} from '@/composables/use-rails-urls.js'
import {useCurrentUser} from '@/composables/use-current-user.js'
import {useVersionCheck} from '@/composables/use-version-check.js'
import {useErrorHandling} from '@/composables/use-error-handling.js'
import {useTimezoneDetection} from '@/composables/use-timezone-detection.js'
import {useEventListener} from '@vueuse/core'
import {ref, watchEffect, onMounted} from 'vue'

//Refs
const hasError = ref(false)
const notAuthenticated = ref(false)

//Composables
const {theme} = useAppTheme()
const {pushRoute} = useRouting()
const {user, updateUser} = useCurrentUser()
const {setDocumentTheme} = useDocument()
const {isLoading, setLoading} = useLoading(true)
const {browserTimezone} = useTimezoneDetection()
const {isProduction} = useEnv()
const {railsAppRootUrl} = useRailsUrls()
const {startVersionCheck} = useVersionCheck()
const {isInTeams} = useTeams()
const {processError, registerGlobalErrorListeners} = useErrorHandling()
const {mountApp} = useAppMount()

//Start version check
startVersionCheck()

//Register global error listeners
registerGlobalErrorListeners()

//Regex to match enrollment IDs in the API base URL
const enrollmentRegex = new RegExp(
  `${railsAppRootUrl.value}/e/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})`
)

//Global click handler to handle both internal and external links (present in markdown)
//This intercepts magic links to the main app (like https://arist.app/e/84fdabd3-7e3f-4f71-a17d-ff25d8d84a0d)
//and re-routes those to the enrollment details page in the messenger app itself.
//For all other external links, it force opens them in a new window to bypass
//any security restrictions on opening those in the iframe of the Teams tab app.
//Teams app deep links are left alone to let Teams handle them appropriately.
//NOTE: There was some work one to render external links in an iframe, in case that's
//ever needed again, have a look at https://github.com/aristco/messenger-ui/pull/97/changes
useEventListener(document, 'click', e => {

  //Not a link target
  if (e.target.tagName.toLowerCase() !== 'a') {
    return
  }

  //Get link and check what it matches
  const url = e.target.href
  const enrollmentMatch = url.match(enrollmentRegex)
  const isTeamsUrl = url.startsWith('https://teams.microsoft.com/l/entity')

  //Magic link to enrollment details page
  if (enrollmentMatch) {
    e.preventDefault()
    const enrollmentId = enrollmentMatch[1]
    pushRoute({name: 'manage-enrollment', params: {enrollmentId}})
  }

  //External link, open in new window
  else if (isInTeams.value && !isTeamsUrl) {
    e.preventDefault()
    openUrl(e.target.href, null, true)
  }
})

//Watch for theme changes
watchEffect(() => setDocumentTheme(theme.value))

//Reload page after 30 seconds if error encountered
watchEffect(() => {
  if (hasError.value && isProduction.value) {
    setTimeout(() => window.location.reload(), 30000)
  }
})

//Mount logic
onMounted(async () => {

  //Mount app
  try {
    const result = await mountApp()
    if (result.outcome === 'deferred') {
      return
    }

    //Clear loading state
    setLoading(false)
  }
  catch (error) {
    processError(error)
    if (error.isUnauthenticatedError) {
      notAuthenticated.value = true
    }
    else {
      hasError.value = true
    }
  }
})

//Update user timezone
watchEffect(() => {
  if (user.value && !user.value.timezone && browserTimezone.value) {
    updateUser({timezone: browserTimezone.value})
  }
})
</script>

<template>
  <TransitionFadeIn>
    <AppLoader v-if="isLoading || hasError || notAuthenticated">
      <AppError
        v-if="hasError"
        error-title="Sorry, there was an error loading the app"
        error-message="Please try reloading the page or try again later."
      />
      <AppError
        v-else-if="notAuthenticated && isInTeams"
        error-title="We couldn’t authenticate your Teams account yet"
        error-message="To get started, please make sure your administrator has provided you with the necessary permissions."
      />
      <AppError
        v-else-if="notAuthenticated"
        error-title="Not authenticated"
        error-message="Redirecting you to the login page..."
        :show-support-link="false"
      />
    </AppLoader>
  </TransitionFadeIn>
  <RouterView
    v-if="!isLoading && !hasError"
    :key="String($route.path)"
  />
  <FlashMessageContainer />
</template>
